/**
 * Utility functions for image cropping
 */

/**
 * Crop an image to a specific aspect ratio
 *
 * @param {string} imageUrl - URL of the image to crop
 * @param {number} aspectRatio - The target aspect ratio (width/height)
 * @param {Object} sentry - Optional sentry instance for error tracking
 * @param {string} componentName - Optional component name for error tracking
 * @returns {Promise<File|null>} - A File object representing the cropped image, or null if cropping failed
 */
export const cropImageToAspectRatio = async (
  imageUrl,
  aspectRatio,
  sentry = null,
  componentName = 'ImageCropping'
) => {
  try {
    // Create an image element to load the image
    const img = new Image()
    img.crossOrigin = 'anonymous'

    // Wait for the image to load
    await new Promise((resolve, reject) => {
      img.onload = resolve
      img.onerror = reject
      img.src = imageUrl
    })

    // Calculate crop dimensions
    const imgW = img.width
    const imgH = img.height
    let cropW, cropH

    // Determine if this is portrait orientation (ratio < 1) or landscape (ratio >= 1)
    const isPortrait = aspectRatio < 1

    // Calculate the crop dimensions
    if (isPortrait) {
      // For portrait orientation, prioritize height
      cropH = imgH
      cropW = cropH * aspectRatio

      // If width exceeds image width, recalculate
      if (cropW > imgW) {
        cropW = imgW
        cropH = cropW / aspectRatio
      }
    } else {
      // For landscape or square
      if (imgW / imgH >= aspectRatio) {
        cropH = imgH
        cropW = cropH * aspectRatio
      } else {
        cropW = imgW
        cropH = cropW / aspectRatio
      }
    }

    // Calculate center position
    const x = (imgW - cropW) / 2
    const y = (imgH - cropH) / 2

    // Create a canvas for the cropped image
    const canvas = document.createElement('canvas')
    canvas.width = cropW
    canvas.height = cropH

    const ctx = canvas.getContext('2d')
    if (!ctx) {
      throw new Error('Failed to get canvas context')
    }

    // Draw the cropped image
    ctx.drawImage(
      img,
      x,
      y,
      cropW,
      cropH, // Source rectangle
      0,
      0,
      cropW,
      cropH // Destination rectangle
    )

    // Convert canvas to blob/file
    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob)
          else reject(new Error('Failed to create blob from canvas'))
        },
        'image/jpeg',
        0.9
      )
    })

    // Create a file from the blob
    const fileName = `cropped-image-${Date.now()}.jpg`
    return new File([blob], fileName, { type: 'image/jpeg', lastModified: Date.now() })
  } catch (error) {
    // Log error if sentry is provided
    if (sentry) {
      sentry.captureImageError(
        error,
        'cropImageToAspectRatio',
        {
          imageUrl,
          aspectRatio,
        },
        componentName
      )
    } else {
      console.error('Error cropping image:', error)
    }
    return null
  }
}

/**
 * Draw a preview of how an image would look when cropped to specific aspect ratio
 *
 * @param {string} imageUrl - URL of the image to preview
 * @param {HTMLCanvasElement} canvas - Canvas element to draw the preview on
 * @param {number} aspectRatio - The target aspect ratio (width/height)
 * @param {Object} sentry - Optional sentry instance for error tracking
 * @param {string} componentName - Optional component name for error tracking
 * @returns {Promise<boolean>} - True if preview was successful, false otherwise
 */
export const drawCroppedPreview = async (
  imageUrl,
  canvas,
  aspectRatio,
  sentry = null,
  componentName = 'ImageCropping'
) => {
  try {
    // Create an image element to load the image
    const img = new Image()
    img.crossOrigin = 'anonymous'

    // Wait for the image to load
    await new Promise((resolve, reject) => {
      img.onload = resolve
      img.onerror = reject
      img.src = imageUrl
    })

    // Calculate crop dimensions
    const imgW = img.width
    const imgH = img.height
    let cropW, cropH

    // Determine if this is portrait orientation (ratio < 1) or landscape (ratio >= 1)
    const isPortrait = aspectRatio < 1

    // Calculate the crop dimensions
    if (isPortrait) {
      // For portrait orientation, prioritize height
      cropH = imgH
      cropW = cropH * aspectRatio

      // If width exceeds image width, recalculate
      if (cropW > imgW) {
        cropW = imgW
        cropH = cropW / aspectRatio
      }
    } else {
      // For landscape or square
      if (imgW / imgH >= aspectRatio) {
        cropH = imgH
        cropW = cropH * aspectRatio
      } else {
        cropW = imgW
        cropH = cropW / aspectRatio
      }
    }

    // Calculate center position
    const x = (imgW - cropW) / 2
    const y = (imgH - cropH) / 2

    // Set canvas dimensions to match crop dimensions
    canvas.width = cropW
    canvas.height = cropH

    const ctx = canvas.getContext('2d')
    if (!ctx) {
      throw new Error('Failed to get canvas context')
    }

    // Draw the cropped portion
    ctx.drawImage(
      img,
      x,
      y,
      cropW,
      cropH, // Source rectangle
      0,
      0,
      cropW,
      cropH // Destination rectangle
    )

    return true
  } catch (error) {
    // Log error if sentry is provided
    if (sentry) {
      sentry.captureImageError(
        error,
        'drawCroppedPreview',
        {
          imageUrl,
          aspectRatio,
        },
        componentName
      )
    } else {
      console.error('Error drawing cropped preview:', error)
    }
    return false
  }
}
